import { useQuery } from '@tanstack/react-query';
import { Outlet, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { CardId, ProjectId, SquadId } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { projectsRepository, squadsRepository, type TaskWithCard } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useOrgContext } from '@web/ui/core';
import { Scaffold } from '@web/ui/core/organisms';
import { ConversationsBoard } from '@web/ui/board/components/conversations_board';
import { BoardTopBar } from '@web/ui/board/components/top_bar';

import { MyTasksHero } from '../components/my_tasks_hero';
import { TasksBoard } from '../components/tasks_board';
import { useBoard, type BoardMode } from '../hooks/use_board';
import { fireAndForget } from '@web/ui/core/utils';

function BoardBody({
  mode,
  conversationQuery,
  taskQuery,
  cardColumns,
  taskColumns,
  myTasks,
  userName,
  onOpenDrawer,
  onSelectTask,
}: Readonly<{
  mode: BoardMode;
  conversationQuery: ReturnType<typeof useBoard>['conversationQuery'];
  taskQuery: ReturnType<typeof useBoard>['taskQuery'];
  cardColumns: ReturnType<typeof useBoard>['cardColumns'];
  taskColumns: ReturnType<typeof useBoard>['taskColumns'];
  myTasks: ReturnType<typeof useBoard>['myTasks'];
  userName: ReturnType<typeof useBoard>['userName'];
  onOpenDrawer: (cardId: CardId) => void;
  onSelectTask: (task: TaskWithCard) => void;
}>): React.JSX.Element | null {
  if (mode === 'conversations') {
    if (conversationQuery.isLoading)
      return <p className="text-meta text-muted-foreground">{t('home.kanban.loading')}</p>;
    if (conversationQuery.isError)
      return <p className="text-meta text-tone-red-fg">{t('home.kanban.error')}</p>;
    if (!conversationQuery.data) return null;
    return (
      <ConversationsBoard
        data={conversationQuery.data}
        cardColumns={cardColumns}
        selectedCardId={null}
        onSelect={onOpenDrawer}
      />
    );
  }
  if (taskQuery.isLoading)
    return <p className="text-meta text-muted-foreground">{t('home.kanban.loading')}</p>;
  if (taskQuery.isError)
    return <p className="text-meta text-tone-red-fg">{t('home.kanban.error')}</p>;
  if (!taskQuery.data) return null;
  return (
    <div className="flex flex-col gap-4">
      <MyTasksHero tasks={myTasks} userName={userName} onSelect={onSelectTask} />
      <TasksBoard data={taskQuery.data} taskColumns={taskColumns} onSelect={onSelectTask} />
    </div>
  );
}

/**
 * Squad-scoped board page. Mounts at `/orgs/$orgSlug/squad/$squadId`.
 * Pill switch Conversas/Tarefas writes the selection to the `?view=`
 * search param so refresh and deep-links survive the toggle; selecting
 * a card pushes the drawer route `/squad/$squadId/card/$cardId/$tab`
 * which renders via `<Outlet />`.
 */
export function BoardPage(): React.JSX.Element {
  const { squadId: squadIdParam } = useParams({ strict: false });
  const squadId = squadIdParam as SquadId;
  const { orgId, orgSlug } = useOrgContext();
  const navigate = useNavigate();
  const { view } = useSearch({ strict: false });
  const mode: BoardMode = view === 'tasks' ? 'tasks' : 'conversations';

  const setMode = (next: BoardMode): void => {
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/squad/$squadId',
        params: { orgSlug, squadId },
        search: { view: next },
      }),
    );
  };

  const { data: squad } = useQuery({
    queryKey: queryKeys.squads.byId(orgId, squadId),
    queryFn: () => squadsRepository.getSquadById(orgId, squadId),
  });
  const projectId = squad?.projectId;
  const { data: project } = useQuery({
    queryKey: queryKeys.projects.detail(orgId, projectId as ProjectId),
    queryFn: () => projectsRepository.getProject(orgId, projectId as ProjectId),
    enabled: !!projectId,
  });

  const { conversationQuery, taskQuery, myTasks, userName, cardColumns, taskColumns } =
    useBoard(squadId);

  const openDrawer = (cardId: CardId): void => {
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/squad/$squadId/card/$cardId/$tab',
        params: { orgSlug, squadId, cardId, tab: 'conversation' },
        // Keep the board's Conversas/Tarefas filter so closing the drawer
        // returns to the same view the card was opened from.
        search: { view: mode },
      }),
    );
  };

  const onSelectTask = (task: TaskWithCard): void => {
    // Open the card drawer with this task in focus (master-detail).
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/squad/$squadId/card/$cardId/$tab/task/$taskId',
        params: { orgSlug, squadId, cardId: task.cardId, tab: 'tasks', taskId: task.id },
        search: { view: mode },
      }),
    );
  };

  return (
    <>
      <Scaffold
        appBar={
          <BoardTopBar
            mode={mode}
            onChangeMode={setMode}
            projectName={project?.name ?? ''}
            squadName={squad?.name ?? ''}
          />
        }
        body={
          <BoardBody
            mode={mode}
            conversationQuery={conversationQuery}
            taskQuery={taskQuery}
            cardColumns={cardColumns}
            taskColumns={taskColumns}
            myTasks={myTasks}
            userName={userName}
            onOpenDrawer={openDrawer}
            onSelectTask={onSelectTask}
          />
        }
      />
      <Outlet />
    </>
  );
}
